import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaCog } from "react-icons/fa";
import Swal from "sweetalert2";
import { CompanyFormsManageActions } from "./CompanyFormsManageActions";
import CompanyFormsTableColumnSettingsModal from "./CompanyFormsTableColumnSettingsModal";
import {
  useCompanyFormsTableColumns,
  COMPANY_FORMS_APPROVE_TABLE_COLUMNS_KEY,
  COMPANY_FORMS_UPLOAD_TABLE_COLUMNS_KEY,
  COMPANY_FORMS_TABLE_COLUMNS,
} from "./companyFormsTableColumns";
import {
  canBulkDeleteCompanyForms,
  canDeleteCompanyFormVersion,
  canEditCompanyForm,
  formatFormDate,
  formatVersionStatusShort,
  formHasPendingVersion,
  openFormFile,
  resolveCategoryLabel,
  resolveDestinationCountry,
} from "./companyFormConstants";

function rowKey({ form, version }) {
  return `${form?.id}-${version?.id}`;
}

function rowStatusBadgeClass(status) {
  if (status === "pending_approval") return "bg-amber-50 text-amber-800";
  if (status === "published") return "bg-emerald-50 text-emerald-700";
  if (status === "rejected") return "bg-red-50 text-red-700";
  if (status === "superseded") return "bg-slate-100 text-slate-600";
  return "bg-slate-100 text-slate-600";
}

function rowDestinationBadgeClass(destinationCountry) {
  const isGlobal =
    !destinationCountry ||
    String(destinationCountry).trim().toLowerCase() === "global";
  return isGlobal ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700";
}

function rowCategoryBadgeClass(category) {
  if (category === "terms_conditions") return "bg-indigo-50 text-indigo-700";
  if (category === "service_agreement") return "bg-violet-50 text-violet-700";
  return "bg-slate-100 text-slate-600";
}

export default function CompanyFormsVersionTable({
  manageRows,
  categoryLabels,
  permissions,
  canUpload,
  canApprove,
  submitting,
  onEditForm,
  onDeleteVersion,
  uploadActionsOnly = false,
  tableMode = "upload",
  onBulkApprove,
  onBulkReject,
  onBulkDelete,
}) {
  const permissionBag = permissions ?? { upload_company_forms: canUpload, approve_company_forms: canApprove };
  const showBulkDelete = Boolean(onBulkDelete && canBulkDeleteCompanyForms(permissionBag));

  const storageKey =
    tableMode === "approve"
      ? COMPANY_FORMS_APPROVE_TABLE_COLUMNS_KEY
      : COMPANY_FORMS_UPLOAD_TABLE_COLUMNS_KEY;

  const {
    visibility,
    hideableColumns,
    toggleColumn,
    resetToDefaults,
    isColumnVisible,
    settingsOpen,
    setSettingsOpen,
  } = useCompanyFormsTableColumns(storageKey);

  const [selectedKeys, setSelectedKeys] = useState(() => new Set());

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [manageRows]);

  const pageRowKeys = useMemo(
    () => manageRows.map((row) => rowKey(row)),
    [manageRows],
  );

  const selectedCount = selectedKeys.size;
  const allPageSelected =
    pageRowKeys.length > 0 && pageRowKeys.every((key) => selectedKeys.has(key));

  const toggleRow = (key, checked) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const toggleAllPage = (checked) => {
    setSelectedKeys(checked ? new Set(pageRowKeys) : new Set());
  };

  const selectedRows = useMemo(
    () => manageRows.filter((row) => selectedKeys.has(rowKey(row))),
    [manageRows, selectedKeys],
  );

  const runBulkDelete = async () => {
    if (!selectedRows.length || !onBulkDelete || !showBulkDelete) return;
    const result = await Swal.fire({
      title: `Delete ${selectedRows.length} selected?`,
      text: "This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    await onBulkDelete(selectedRows);
    setSelectedKeys(new Set());
  };

  const runBulkApprove = async () => {
    if (!selectedRows.length || !onBulkApprove) return;
    const pendingRows = selectedRows.filter(
      (r) => r.version?.status === "pending_approval",
    );
    if (!pendingRows.length) {
      Swal.fire("Nothing to approve", "Select rows that are pending review.", "info");
      return;
    }
    const result = await Swal.fire({
      title: `Approve ${pendingRows.length} document${pendingRows.length > 1 ? "s" : ""}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#007BFF",
      confirmButtonText: "Approve",
    });
    if (!result.isConfirmed) return;
    await onBulkApprove(pendingRows);
    setSelectedKeys(new Set());
  };

  const runBulkReject = async () => {
    if (!selectedRows.length || !onBulkReject) return;
    const pendingRows = selectedRows.filter(
      (r) => r.version?.status === "pending_approval",
    );
    if (!pendingRows.length) {
      Swal.fire("Nothing to reject", "Select rows that are pending review.", "info");
      return;
    }
    const result = await Swal.fire({
      title: `Reject ${pendingRows.length} upload${pendingRows.length > 1 ? "s" : ""}?`,
      input: "textarea",
      inputPlaceholder: "Optional note for uploaders…",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Reject",
    });
    if (!result.isConfirmed) return;
    await onBulkReject(pendingRows, result.value || "");
    setSelectedKeys(new Set());
  };

  const headerColumns = useMemo(() => {
    const keys = ["select"];
    if (isColumnVisible("document")) keys.push("document");
    if (isColumnVisible("destination")) keys.push("destination");
    if (isColumnVisible("category")) keys.push("category");
    if (isColumnVisible("uploader")) keys.push("uploader");
    if (isColumnVisible("status")) keys.push("status");
    if (isColumnVisible("rejection_note")) keys.push("rejection_note");
    if (isColumnVisible("actions")) keys.push("actions");
    return keys;
  }, [isColumnVisible]);

  const columnTitle = (key) =>
    COMPANY_FORMS_TABLE_COLUMNS.find((c) => c.key === key)?.title ?? key;

  const renderCell = useCallback(
    (columnKey, row) => {
      const { form, version } = row;
      const categoryLabel = resolveCategoryLabel(form.category, categoryLabels);
      const destinationLabel = resolveDestinationCountry(form.destination_country);
      const hasPending = formHasPendingVersion(form);
      const isLatestForForm = form.versions?.[0]?.id === version.id;
      const uploaderName = version.uploaded_by?.name || "—";
      const statusLabel = formatVersionStatusShort(version.status, {
        isCurrent: version.is_current && version.status === "published",
      });
      const pill =
        "inline-flex max-w-full truncate rounded-md px-1.5 py-0.5 text-[10px] font-semibold leading-tight";
      const td =
        "border-b border-slate-100/90 py-2.5 align-middle";

      switch (columnKey) {
        case "document":
          return (
            <td className={`${td} max-w-0 px-2.5`}>
              <p
                className="truncate text-xs font-semibold text-slate-900"
                title={form.name}
              >
                {form.name}
              </p>
            </td>
          );
        case "destination":
          return (
            <td className={`${td} px-2.5`}>
              <span className={`${pill} ${rowDestinationBadgeClass(form.destination_country)}`}>
                {destinationLabel}
              </span>
            </td>
          );
        case "category":
          return (
            <td className={`${td} px-2.5`}>
              <span
                className={`${pill} uppercase tracking-wide ${rowCategoryBadgeClass(form.category)}`}
              >
                {categoryLabel || "—"}
              </span>
            </td>
          );
        case "uploader":
          return (
            <td className={`${td} px-2.5`}>
              <p className="truncate text-xs font-medium text-slate-800">{uploaderName}</p>
              <p className="mt-0.5 text-[10px] text-slate-500">
                {formatFormDate(version.created_at)}
              </p>
            </td>
          );
        case "status":
          return (
            <td
              className={`${td} w-[4.5rem] min-w-[4.5rem] max-w-[4.75rem] overflow-hidden px-1.5`}
            >
              <span
                className={`${pill} ${rowStatusBadgeClass(version.status)}`}
                title={statusLabel}
              >
                {statusLabel}
              </span>
            </td>
          );
        case "rejection_note":
          return (
            <td className={`${td} max-w-0 px-2.5`}>
              {version.status === "rejected" || version.rejection_note ? (
                <>
                  <p
                    className="line-clamp-2 text-[11px] leading-snug text-slate-700"
                    title={version.rejection_note || undefined}
                  >
                    {version.rejection_note?.trim() || "No note"}
                  </p>
                  {version.rejected_at ? (
                    <p className="mt-0.5 truncate text-[10px] text-slate-400">
                      {version.rejected_by?.name ? `${version.rejected_by.name} · ` : ""}
                      {formatFormDate(version.rejected_at)}
                    </p>
                  ) : null}
                </>
              ) : (
                <span className="text-[11px] text-slate-400">—</span>
              )}
            </td>
          );
        case "actions":
          return (
            <td className={`${td} w-14 min-w-14 pl-1 pr-0 text-right`}>
              <CompanyFormsManageActions
                version={version}
                submitting={submitting}
                canEditForm={
                  !uploadActionsOnly &&
                  isLatestForForm &&
                  canEditCompanyForm(form, permissionBag, version)
                }
                canDeleteVersion={canDeleteCompanyFormVersion(version, permissionBag)}
                onOpenFile={openFormFile}
                onEditForm={() => onEditForm(form)}
                onDeleteVersion={() => onDeleteVersion(form, version)}
              />
            </td>
          );
        default:
          return null;
      }
    },
    [
      categoryLabels,
      permissions,
      canUpload,
      canApprove,
      submitting,
      uploadActionsOnly,
      onEditForm,
      onDeleteVersion,
      permissionBag,
    ],
  );

  const thBase =
    "border-b border-[#E5ECF5] bg-[#F8FAFC] py-2.5 text-[10px] font-bold uppercase tracking-wide text-[#64748B]";

  const thClassFor = (key) => {
    switch (key) {
      case "select":
        return `${thBase} w-10 min-w-10 max-w-10 pl-0 pr-1 text-center`;
      case "document":
        return `${thBase} w-[26%] px-2.5`;
      case "destination":
        return `${thBase} w-[10%] px-2.5`;
      case "category":
        return `${thBase} w-[14%] px-2.5`;
      case "uploader":
        return `${thBase} w-[12%] px-2.5`;
      case "status":
        return `${thBase} w-[4.5rem] min-w-[4.5rem] max-w-[4.75rem] px-1.5`;
      case "rejection_note":
        return `${thBase} px-2.5`;
      case "actions":
        return `${thBase} w-14 min-w-14 pl-1 pr-0 text-right`;
      default:
        return `${thBase} px-2.5`;
    }
  };

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-[#E5ECF5] bg-white shadow-[0_1px_2px_rgba(20,33,61,0.05)]">
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-[#E5ECF5] bg-[#F8FAFC] px-4 py-2.5 sm:px-5"
          role="toolbar"
          aria-label="Table toolbar"
        >
          <p className="shrink-0 text-[11px] font-semibold text-[#475569]">
            {manageRows.length > 0
              ? `${manageRows.length} on this page · ${selectedCount} selected`
              : "No rows"}
          </p>

          {selectedCount > 0 ? (
            <div className="flex flex-wrap items-center gap-2 border-l border-slate-200/90 pl-3">
              <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                Bulk
                <span className="ml-0.5 font-semibold text-blue-600">({selectedCount})</span>
              </span>
              {tableMode === "approve" ? (
                <>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={runBulkApprove}
                    className="rounded-md bg-[#007BFF] px-2.5 py-1 text-[11px] font-semibold text-white transition hover:bg-[#0069d9] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={runBulkReject}
                    className="rounded-md border border-red-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Reject
                  </button>
                </>
              ) : null}
              {showBulkDelete ? (
                <button
                  type="button"
                  disabled={submitting}
                  onClick={runBulkDelete}
                  className="rounded-md border border-red-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Delete
                </button>
              ) : null}
            </div>
          ) : null}

          <button
            type="button"
            className="ml-auto inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 hover:text-[#007BFF]"
            onClick={() => setSettingsOpen(true)}
            title="Column settings"
            aria-label="Column settings"
          >
            <FaCog size={13} aria-hidden />
          </button>
        </div>

        <div className="overflow-x-auto px-4 pb-3 pt-2 sm:px-5">
          <table className="w-full min-w-[720px] table-fixed border-collapse text-left text-xs font-medium text-slate-800 [&_tbody_tr:last-child_td]:border-b-0">
            <thead>
              <tr>
                {headerColumns.map((key) =>
                  key === "select" ? (
                    <th key={key} className={thClassFor("select")}>
                      <input
                        type="checkbox"
                        className="m-0 h-3.5 w-3.5 shrink-0 cursor-pointer accent-[#007BFF]"
                        checked={allPageSelected}
                        onChange={(e) => toggleAllPage(e.target.checked)}
                        aria-label="Select all on this page"
                      />
                    </th>
                  ) : (
                    <th key={key} className={thClassFor(key)}>
                      {columnTitle(key)}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {manageRows.map((row) => {
                const key = rowKey(row);
                return (
                  <tr key={key} className="transition-colors hover:bg-slate-50/90">
                    <td className="w-10 min-w-10 max-w-10 border-b border-slate-100/90 py-2.5 pl-0 pr-1 text-center align-middle">
                      <input
                        type="checkbox"
                        className="m-0 h-3.5 w-3.5 shrink-0 cursor-pointer accent-[#007BFF]"
                        checked={selectedKeys.has(key)}
                        onChange={(e) => toggleRow(key, e.target.checked)}
                        aria-label={`Select ${row.form?.name || "document"}`}
                      />
                    </td>
                    {headerColumns
                      .filter((col) => col !== "select")
                      .map((col) => (
                        <React.Fragment key={col}>{renderCell(col, row)}</React.Fragment>
                      ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <CompanyFormsTableColumnSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        columns={hideableColumns}
        visibility={visibility}
        onToggle={toggleColumn}
        onReset={resetToDefaults}
      />
    </>
  );
}
